import { categorySeoIndexable } from '@mts/shared/categorySeoPolicy';
import { usePublicMetadata } from '../seo/PublicMetadata.jsx';
import { useInitialPublicData } from '../seo/PublicBootstrap.jsx';
import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDirectoryListings } from '../hooks/useDirectoryListings';
import BusinessTagFilter from '../components/directory/BusinessTagFilter.jsx';
import { useDirectorySearchState } from '../hooks/useDirectorySearchState';
import { usePublicTenant } from '../hooks/usePublicTenant';
import FilterBar from '../components/directory/FilterBar';
import DirectoryHeader from '../components/directory/DirectoryHeader';
import DirectoryNavigationHeader from '../components/directory/DirectoryNavigationHeader';
import DirectoryResultsState from '../components/directory/DirectoryResultsState';
import DirectoryResultsGrid from '../components/directory/DirectoryResultsGrid';
import { fetchCategories } from '../services/api';
import { buildListingPath } from '../utils/listingUrls';
import { buildPublicPageMetadata } from '../utils/publicMetadata.js';
import { buildBreadcrumbSchema, buildItemListSchema, buildWebPageSchema } from '../utils/structuredData.js';

const DirectoryMap = lazy(() => import('../components/directory/DirectoryMap.jsx'));
const LISTINGS_PER_PAGE = 10;

function DirectoryPagination({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
}) {
  const [params] = useSearchParams();
  const hrefForPage = page => {
    const next = new URLSearchParams(params);
    if (page === 1) next.delete('page'); else next.set('page', String(page));
    return `/listings${next.size ? `?${next}` : ''}`;
  };
  if (totalPages <= 1) return null;

  const navigatePage = page => event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault(); onPageChange(page);
  };
  const firstResult = (currentPage - 1) * pageSize + 1;
  const lastResult = Math.min(currentPage * pageSize, totalCount);
  const pageNumbers = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages])]
    .filter(page => page >= 1 && page <= totalPages).sort((a, b) => a - b);

  return (
    <nav className="mt-8 flex flex-col gap-4 border-t border-base-300 pt-6 sm:flex-row sm:items-center sm:justify-between" aria-label="Listings pagination">
      <p className="text-sm text-base-content/70">
        Showing {firstResult}-{lastResult} of {totalCount}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {currentPage > 1 ? <a rel="prev" href={hrefForPage(currentPage - 1)} onClick={navigatePage(currentPage - 1)} className="inline-flex h-10 items-center justify-center rounded-md border border-base-300 bg-base-100 px-3 text-sm font-medium text-base-content transition hover:bg-base-200">Previous</a> : <span aria-disabled="true" className="inline-flex h-10 items-center justify-center rounded-md border border-base-300 bg-base-100 px-3 text-sm font-medium text-base-content transition hover:bg-base-200 opacity-50">Previous</span>}

        {pageNumbers.map((page, index) => {
          const previousPage = pageNumbers[index - 1];
          const showGap = previousPage && page - previousPage > 1;

          return (
            <React.Fragment key={page}>
              {showGap ? <span className="px-1 text-sm text-base-content/50">...</span> : null}
              <a
                href={hrefForPage(page)}
                onClick={navigatePage(page)}
                aria-current={page === currentPage ? 'page' : undefined}
                className={[
                  'inline-flex h-10 min-w-10 items-center justify-center rounded-md border px-3 text-sm font-medium transition',
                  page === currentPage
                    ? 'border-primary bg-primary text-primary-content'
                    : 'border-base-300 bg-base-100 text-base-content hover:bg-base-200',
                ].join(' ')}
              >
                {page}
              </a>
            </React.Fragment>
          );
        })}

        {currentPage < totalPages ? <a rel="next" href={hrefForPage(currentPage + 1)} onClick={navigatePage(currentPage + 1)} className="inline-flex h-10 items-center justify-center rounded-md border border-base-300 bg-base-100 px-3 text-sm font-medium text-base-content transition hover:bg-base-200">Next</a> : <span aria-disabled="true" className="inline-flex h-10 items-center justify-center rounded-md border border-base-300 bg-base-100 px-3 text-sm font-medium text-base-content transition hover:bg-base-200 opacity-50">Next</span>}
      </div>
    </nav>
  );
}

function DirectoryPage() {
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => { setMapReady(true); }, []);
  const initialCategories = useInitialPublicData("categories");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { listings, loading, error, total, retry } = useDirectoryListings(searchParams);
  const filteredListings = listings;
  const viewMode = searchParams.get('view') === 'list' ? 'list' : 'map';
  const setViewMode = (view) => setSearchParams(current => {
    const next = new URLSearchParams(current);
    const query = searchQuery.trim();
    if (query !== (current.get('q') || '')) next.delete('page');
    if (query) next.set('q', query); else next.delete('q');
    if (view === 'list') next.set('view', 'list'); else next.delete('view');
    return next;
  });

  const {
    searchQuery,
    committedQuery,
    setSearchQuery,
    applySearchTerm,
    categoryFilter,
    setCategoryFilter,
    sortBy,
    setSortBy,
    showOpenOnly,
    setShowOpenOnly,
    isDesktopCardLayout,
    recentSearches,
    mapListingToCardListing,
    resetFilters,
  } = useDirectorySearchState();

  const { tenant, spotlightName, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const requestedPage = Number.parseInt(searchParams.get('page') || '1', 10);
  const totalPages = Math.max(1, Math.ceil(total / LISTINGS_PER_PAGE));
  const currentPage = Number.isFinite(requestedPage)
    ? Math.min(Math.max(requestedPage, 1), totalPages)
    : 1;
  const pageStartIndex = (currentPage - 1) * LISTINGS_PER_PAGE;
  const paginatedListings = listings;

  const [tenantCategories, setTenantCategories] = useState(initialCategories || []);
  useEffect(() => {
    if (initialCategories) { setTenantCategories(initialCategories); return; }
    let active = true;
    fetchCategories(categoryFilter)
      .then((data) => { if (active) setTenantCategories(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setTenantCategories([]); });
    return () => { active = false; };
  }, [initialCategories, categoryFilter]);

  const handleSearch = (query) => {
    applySearchTerm(query);
  };

  const setCurrentPage = useCallback((page, options = {}) => {
    const { replace = false, scroll = true } = options;
    const nextPage = Math.min(Math.max(page, 1), totalPages);

    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);

      if (nextPage <= 1) {
        nextParams.delete('page');
      } else {
        nextParams.set('page', String(nextPage));
      }

      return nextParams;
    }, { replace });

    if (scroll && typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [setSearchParams, totalPages]);

  const handleBusinessClick = (business) => {
    const path = buildListingPath(business);
    if (path) navigate(path);
  };

  useEffect(() => {
    if (!loading && !error && requestedPage !== currentPage) {
      setCurrentPage(currentPage, { replace: true, scroll: false });
    }
  }, [loading, error, requestedPage, currentPage]);

  usePublicMetadata(() => {
    if (!tenant || loading) return;
    const selectedCategory = tenantCategories.find((item) => [item.id, item.slug, item.name].includes(categoryFilter));
    const categoryName = categoryFilter === 'all' ? '' : selectedCategory?.name || String(categoryFilter);
    const canonicalSearch = new URLSearchParams();
    if (categoryFilter !== 'all') canonicalSearch.set('category', selectedCategory?.slug || categoryFilter);
    if (currentPage > 1) canonicalSearch.set('page', String(currentPage));
    const path = `/listings${canonicalSearch.size ? `?${canonicalSearch}` : ''}`;
    const city = tenantLogoConfig?.cityName;
    const title = categoryName ? `${categoryName} in ${city}${currentPage > 1 ? ` — Page ${currentPage}` : ''} | ${spotlightName}` : `${city} Business Directory${currentPage > 1 ? ` — Page ${currentPage}` : ''} | ${spotlightName}`;
    const description = selectedCategory?.editorial?.introduction || (categoryName ? `Explore ${categoryName.toLowerCase()} businesses serving ${city}. Find contact details, services, offers, and local recommendations.` : `Explore local businesses serving ${city}. Find contact details, services, offers, and community recommendations.`);
    const metadata = buildPublicPageMetadata({ tenant, pathname: '/listings', search: canonicalSearch.toString(), canonicalPath: path, siteName: spotlightName, cityName: city, logoUrl: tenantLogoImageUrl, title, description, robots: error || committedQuery || ['city', 'openOnly', 'tagIds', 'matchMode'].some(key => searchParams.has(key)) || (searchParams.has('sort') && searchParams.get('sort') !== 'name') || (categoryFilter !== 'all' && !categorySeoIndexable({ editorialQualityApproved: selectedCategory?.editorialQualityApproved, total })) ? 'noindex,follow' : 'index,follow' });
    if (metadata.canonicalUrl && !committedQuery && !searchParams.get('tagIds')) metadata.jsonLd = [buildWebPageSchema({ url: metadata.canonicalUrl, name: title, description, type: 'CollectionPage' }), buildItemListSchema({ url: metadata.canonicalUrl, name: title, items: paginatedListings.map((listing) => ({ name: listing.name, url: new URL(buildListingPath(listing), tenant.canonicalOrigin).toString() })) }), buildBreadcrumbSchema({ url: metadata.canonicalUrl, items: [{ name: 'Home', url: tenant.canonicalOrigin }, { name: 'Business Directory', url: `${tenant.canonicalOrigin}/listings` }, ...(categoryName ? [{ name: categoryName, url: metadata.canonicalUrl }] : [])] })];
    return (metadata);
  }, [total, categoryFilter, currentPage, loading, error, paginatedListings, committedQuery, spotlightName, tenant, tenantCategories, tenantLogoConfig?.cityName, tenantLogoImageUrl, searchParams]);

  return (
    <div className="min-h-screen bg-base-200">
      <DirectoryHeader
        spotlightName={spotlightName}
        searchQuery={searchQuery}
        totalCount={total}
        recentSearches={recentSearches}
        onSearchSubmit={handleSearch}
        onSearchChange={setSearchQuery}
      />

      <DirectoryNavigationHeader
        resultCount={total}
        searchQuery={searchQuery}
      />

      {/* Filters */}
      <div className="bg-base-100/90 backdrop-blur-sm border-b border-base-300 sticky top-16 z-40">
        <div className="container mx-auto max-w-7xl px-6 py-4">
          <BusinessTagFilter/>
        <FilterBar
            categories={tenantCategories}
            categoryValue={categoryFilter}
            onCategoryChange={setCategoryFilter}
            sortValue={sortBy}
            onSortChange={setSortBy}
            openOnlyValue={showOpenOnly}
            onOpenOnlyChange={setShowOpenOnly}
            resultCount={total}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </div>
      </div>

      <section className="container mx-auto max-w-7xl px-6 pt-6" aria-label="Directory introduction">
        <h1 className="text-3xl font-bold text-base-content">{categoryFilter !== 'all' && tenantCategories.find(item => [item.id, item.slug, item.name].includes(categoryFilter))?.name
          ? `${tenantCategories.find(item => [item.id, item.slug, item.name].includes(categoryFilter)).name} in ${tenantLogoConfig?.cityName || spotlightName}`
          : `${tenantLogoConfig?.cityName || spotlightName} Business Directory`}</h1>
        {!loading && !error ? <p className="mt-2 text-base-content/70">{total} published {total === 1 ? 'business' : 'businesses'}{currentPage > 1 ? ` — Page ${currentPage}` : ''}</p> : null}
      </section>

      {categoryFilter !== 'all' && !loading && !error ? (() => {
        const editorial = tenantCategories.find(item => [item.id, item.slug, item.name].includes(categoryFilter))?.editorial;
        if (!editorial) return null;
        return <section className="container mx-auto max-w-7xl space-y-4 px-6 pt-4" aria-label="Local category context">
          <p className="whitespace-pre-line">{editorial.introduction}</p>
          {editorial.localContext ? <p className="whitespace-pre-line">{editorial.localContext}</p> : null}
          {editorial.image ? <img src={editorial.image.url} alt={editorial.image.alt} loading="lazy" className="max-h-96 rounded-lg object-cover" /> : null}
          {editorial.faqs?.length ? <div><h2 className="text-xl font-semibold">Local questions</h2>{editorial.faqs.map((faq, index) => <div key={index} className="mt-3"><h3 className="font-semibold">{faq.question}</h3><p>{faq.answer}</p></div>)}</div> : null}
          {editorial.sources?.length ? <div><h2 className="font-semibold">Sources</h2><ul>{editorial.sources.map((source, index) => <li key={index}><a href={source.url} rel="noopener noreferrer">{source.label || source.url}</a></li>)}</ul></div> : null}
        </section>;
      })() : null}

      {/* Listings */}
      <div className="container mx-auto max-w-7xl px-6 py-8">
        {mapReady && viewMode === 'map' && !error ? <Suspense fallback={<div className="mb-8 rounded-2xl border border-base-300 bg-base-100 p-8" role="status">Loading map…</div>}>
          <DirectoryMap listings={paginatedListings} loading={loading} onViewProfile={handleBusinessClick} />
        </Suspense> : null}
        {loading ? (
          <DirectoryResultsGrid loading skeletonCount={6} />
        ) : error ? (
          <DirectoryResultsState type="error" message={error} onRetry={retry} />
        ) : filteredListings.length === 0 ? (
          <DirectoryResultsState
            type="empty"
            onClearFilters={resetFilters}
          />
        ) : (
          <DirectoryResultsGrid
            listings={paginatedListings}
            isDesktopCardLayout={isDesktopCardLayout}
            onViewProfile={handleBusinessClick}
            mapListingToCardListing={mapListingToCardListing}
            startIndex={pageStartIndex}
          />
        )}
        {!loading && !error && <DirectoryPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={total}
          pageSize={LISTINGS_PER_PAGE}
          onPageChange={setCurrentPage}
        />}
      </div>
    </div>
  );
}

export default DirectoryPage;
